import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface SeedJobAd {
  id: number;
  seedCandidateId: number | null;
  sourceBrand: string | null;
  imageUrl: string;
  adStyle: string | null;
  adAngle: string | null;
  daysLive: number | null;
  analysisStatus: string;
  promotedToLibrary: boolean;
  rejected: boolean;
  promptRecipe: string | null;
  colorPalette: string[] | null;
  composition: string | null;
}

interface SeedBrandCandidate {
  id: number;
  brandName: string;
}

// v5.51 — click cards to stage a selection, Analyze submits it into Winning
// Ads (promote + scan). Unselected ads keep waiting and the job stays open,
// so the user can return to the same seed and pick more later.
export function SeederAdReviewGrid({
  jobId,
  ads,
  candidates: _candidates,
}: {
  jobId: number;
  ads: SeedJobAd[];
  candidates: SeedBrandCandidate[];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/admin/seed-jobs/${jobId}`] });
  };

  const inLibrary = ads.filter((a) => a.promotedToLibrary);
  const reviewable = ads.filter((a) => !a.promotedToLibrary && !a.rejected);

  const toggle = (adId: number) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(adId)) n.delete(adId); else n.add(adId);
      return n;
    });
  };

  const analyze = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/seed-jobs/${jobId}/analyze-selected`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promoteIds: Array.from(selected) }),
      });
      if (!res.ok) {
        let bodyText = "";
        try { bodyText = await res.text(); } catch { /* ignore */ }
        throw new Error(`${res.status}: ${bodyText || res.statusText}`);
      }
      return res.json() as Promise<{ ok: boolean; promoted: number; analysisQueued: number }>;
    },
    onSuccess: (result) => {
      setSelected(new Set());
      invalidate();
      toast({
        title: `${result.promoted} ad${result.promoted === 1 ? "" : "s"} submitted to Winning Ads`,
        description:
          result.analysisQueued > 0
            ? `${result.analysisQueued} analyzing now — they'll file into their niche groups shortly.`
            : "Already analyzed — live in the library now.",
      });
    },
    onError: (err: Error) => {
      setErrorMsg(`Analyze failed: ${err.message}`);
    },
  });

  return (
    <div>
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 rounded mb-4 flex items-start justify-between gap-3">
          <div className="flex-1"><strong>Action failed:</strong> {errorMsg}</div>
          <button
            onClick={() => setErrorMsg(null)}
            className="text-red-600 hover:text-red-800 font-medium text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="sticky top-0 z-10 bg-white border-b border-[#DCE3ED] py-3 -mx-6 px-6 mb-4 flex items-center justify-between gap-3">
        <div className="text-sm">
          <span className="font-medium text-green-700">{selected.size} selected</span>
          <span className="text-[#526074] mx-2">·</span>
          <span className="text-[#526074]">{reviewable.length} to review</span>
          {inLibrary.length > 0 && (
            <>
              <span className="text-[#526074] mx-2">·</span>
              <span className="text-[#526074]">{inLibrary.length} in library</span>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="button-select-all"
            disabled={reviewable.length === 0}
            onClick={() =>
              setSelected(
                selected.size === reviewable.length
                  ? new Set()
                  : new Set(reviewable.map((a) => a.id))
              )
            }
          >
            {selected.size === reviewable.length && reviewable.length > 0 ? "Clear Selection" : `Select All (${reviewable.length})`}
          </Button>
          <Button
            data-testid="button-analyze-selected"
            onClick={() => analyze.mutate()}
            disabled={selected.size === 0 || analyze.isPending}
          >
            {analyze.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Analyze ({selected.size})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {ads.filter((a) => !a.rejected).map((ad) => {
          const isSelected = selected.has(ad.id);
          const isInLibrary = ad.promotedToLibrary;
          return (
            <div
              key={ad.id}
              data-testid={`card-seeder-ad-${ad.id}`}
              onClick={() => { if (!isInLibrary) toggle(ad.id); }}
              className={`relative rounded-lg overflow-hidden transition-shadow ${
                isInLibrary
                  ? "ring-2 ring-green-500 opacity-80"
                  : isSelected
                    ? "ring-4 ring-green-500 cursor-pointer"
                    : "ring-1 ring-gray-200 cursor-pointer hover:ring-2 hover:ring-gray-400"
              }`}
            >
              <img
                src={ad.imageUrl}
                alt=""
                loading="lazy"
                className="w-full h-auto block bg-gray-100"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
              {isSelected && !isInLibrary && (
                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1.5 shadow">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </div>
              )}
              {isInLibrary && (
                <div className="absolute top-2 right-2 bg-green-600 text-white text-[11px] font-medium px-2 py-0.5 rounded shadow">
                  ✓ In Library
                </div>
              )}
              {ad.analysisStatus === "failed" && isInLibrary && (
                <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded">
                  Analysis failed
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
